import { Box, Typography } from "@mui/material"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { AssociateProductSearchSuggestion } from "./AssociateProductSearchSuggestion"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Product } from "@/graphql/codegen/graphql"
import { ProductSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { ProductSearchInfo } from "@/screens/ManageProducts/components/EquivalentProductGroups/components/EquivalentProducts/components/ProductSearchInfo"
import { AssociateProductType } from "@/screens/ManageProducts/components/EquivalentProductGroups/types"
import useAssociateProductColumns from "@/screens/Products/hooks/useAssociateProductColumns"
import useSearchProductsForAssociation from "@/screens/Products/hooks/useSearchProductsForAssociation"
import type { AutocompleteOption } from "@/types/form-fields"

const ASSOCIATE_PRODUCT_SORT_FIELD_MAP: Partial<Record<string, ProductSortField>> = {
  productId: ProductSortField.ProductId,
  companyProductDescription: ProductSortField.ProductSupplierDescription,
  vendorDescription: ProductSortField.ProductVendorDescription,
  vendor: ProductSortField.CompanyName,
  shipFrom: ProductSortField.ShipFromLocation,
}

interface AssociateProductSearchStepProps {
  associatedProductIds: number[]
  selectedProduct: Product | null
  onProductSelect: (product: Product) => void
  submittedQuery: string
  onSubmittedQueryChange: (query: string) => void
  showInactive: boolean
  onShowInactiveChange: (value: boolean) => void
  searchInputValue: string
  onSearchInputValueChange: (value: string) => void
}

export const AssociateProductSearchStep: FC<AssociateProductSearchStepProps> = ({
  associatedProductIds,
  selectedProduct,
  onProductSelect,
  submittedQuery,
  onSubmittedQueryChange,
  showInactive,
  onShowInactiveChange,
  searchInputValue,
  onSearchInputValueChange,
}) => {
  const [sorting, setSorting] = useState<SortingState>([])

  const productSort = useMemo(() => {
    if (!sorting[0]) return undefined
    const field = ASSOCIATE_PRODUCT_SORT_FIELD_MAP[sorting[0].id]
    if (!field) return undefined
    return { field, order: sorting[0].desc ? SortingOrder.Desc : SortingOrder.Asc }
  }, [sorting])

  const { products, isFetchingProducts } = useSearchProductsForAssociation({
    query: submittedQuery,
    showInactive,
    sort: productSort,
  })

  const displayedProducts = products.filter((p) => !associatedProductIds.includes(p.productId))

  const columns = useAssociateProductColumns({
    showRadioButton: true,
    onSelect: onProductSelect,
  })

  const rowSelection = selectedProduct ? { [selectedProduct.productId.toString()]: true } : {}

  const handleSearch = (value: string | AutocompleteOption) => {
    onSubmittedQueryChange(typeof value === "string" ? value : value.label)
  }

  const showSearchResults = submittedQuery.length >= 2

  return (
    <Box className='flex flex-col gap-6'>
      <Typography className='text-ml font-normal text-gray-900'>Look for a Product to Associate</Typography>

      <AssociateProductSearchSuggestion
        showInactive={showInactive}
        onSearch={handleSearch}
        searchInputValue={searchInputValue}
        onInputValueChange={onSearchInputValueChange}
        excludedProductIds={associatedProductIds}
      />

      {showSearchResults && (
        <Box className='flex flex-col gap-2'>
          {!isFetchingProducts && (
            <ProductSearchInfo
              productCount={displayedProducts.length}
              productType={AssociateProductType.Akrochem}
              showInactive={showInactive}
              onShowInactiveToggle={() => onShowInactiveChange(!showInactive)}
            />
          )}
          {!isFetchingProducts && displayedProducts.length === 0 && (
            <Box className='flex flex-col items-center justify-center'>
              <Typography className='text-gray-500'>No products found for "{submittedQuery}"</Typography>
            </Box>
          )}
          {(isFetchingProducts || displayedProducts.length > 0) && (
            <Box className='max-h-96 overflow-auto'>
              <AerosBaseTable
                features={{
                  tanstackOptions: {
                    data: displayedProducts,
                    columns,
                    state: { isLoading: isFetchingProducts, rowSelection, sorting },
                    getRowId: (row) => row.productId.toString(),
                    onSortingChange: setSorting as OnChangeFn<SortingState>,
                    manualSorting: true,
                    enableMultiSort: false,
                    enableSortingRemoval: false,
                  },
                }}
                slotProps={{
                  bodyRow: {
                    className: "cursor-pointer hover:bg-primary-100",
                  },
                }}
                noDataMessage='No products found'
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
